import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { registrationStatusResponseSchema } from '@unit/shared';
import { publicPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ActionError } from '../../components/ActionError.js';
import { useRegistrationOpen } from './useRegistrationOpen.js';

const input =
  'mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';
const primary =
  'w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';

/**
 * The public self-registration form (owner, 2026-09-14, story 33): anyone
 * with the link. Nothing is created until an admin approves the request
 * and places the person on a unit — the page says exactly that.
 */
export function RegisterPage() {
  const { t } = useTranslation();
  const open = useRegistrationOpen();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', personalNumber: '' });
  const [done, setDone] = useState(false);
  const submit = useAction(() => publicPost('/public/registration', registrationStatusResponseSchema, form));
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (await submit.run()) setDone(true);
  };

  return (
    <main className="flex min-h-full items-center justify-center p-6">
      <section className="w-full max-w-md rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200" data-testid="register-page">
        <h1 className="text-xl font-semibold text-slate-900">{t('register.title')}</h1>
        {open === false ? (
          <p className="mt-3 text-sm text-slate-600" data-testid="register-closed">{t('register.closed')}</p>
        ) : done ? (
          <p className="mt-3 text-sm text-emerald-800" data-testid="register-done">{t('register.done')}</p>
        ) : (
          <form onSubmit={(e) => void onSubmit(e)} className="mt-3 flex flex-col gap-3" data-testid="register-form">
            <p className="text-sm text-slate-500">{t('register.intro')}</p>
            <label className="text-sm text-slate-600">
              {t('users.firstName')}
              <input className={input} value={form.firstName} onChange={set('firstName')} required maxLength={80} data-testid="register-first-name" />
            </label>
            <label className="text-sm text-slate-600">
              {t('users.lastName')}
              <input className={input} value={form.lastName} onChange={set('lastName')} required maxLength={80} data-testid="register-last-name" />
            </label>
            <label className="text-sm text-slate-600">
              {t('users.email')}
              <input className={`${input} latin-inline`} type="email" value={form.email} onChange={set('email')} required data-testid="register-email" />
            </label>
            <label className="text-sm text-slate-600">
              {t('users.phone')}
              <input className={`${input} latin-inline`} type="tel" value={form.phone} onChange={set('phone')} required minLength={5} data-testid="register-phone" />
            </label>
            <label className="text-sm text-slate-600">
              {t('users.personalNumber')}
              <input className={`${input} latin-inline`} inputMode="numeric" pattern="[0-9]{5,10}" value={form.personalNumber} onChange={set('personalNumber')} required data-testid="register-personal-number" />
            </label>
            <button type="submit" disabled={submit.busy || open === null} className={primary} data-testid="register-submit">
              {submit.busy ? t('register.sending') : t('register.submit')}
            </button>
            <ActionError error={submit.error} />
            <a href="/" className="text-center text-sm text-slate-600 underline-offset-4 hover:underline">
              {t('register.backToSignIn')}
            </a>
          </form>
        )}
      </section>
    </main>
  );
}
